import { Intent } from "@blueprintjs/core";
import {
    CostAssumption,
    MisoPercentageReduction
} from "in_queue/types/scenarioType";
import { isEqual } from "lodash";
import {
    SensitivitiesCsvAssumptions,
    SensitivitiesCsvResult
} from "../ScenarioDialog/sensitivitiesCsv";

export const MAX_BULK_SCENARIOS = 50;

export interface BulkScenarioSettings {
    readonly costAssumptions: CostAssumption;
    readonly customUpgradeId: string | null;
    readonly runOnSubmit: boolean;
}

export interface BulkScenarioAssumptions {
    readonly projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    readonly includedSpeculativeProjectIds: ReadonlyArray<string>;
}

/** What the upload rules changed in a file, kept as uploaded while its assumptions are edited. */
export type BulkScenarioUploadNotes = Omit<
    SensitivitiesCsvAssumptions,
    "assumptions" | "includedSpeculativeProjectIds" | "skippedRowCount"
>;

export interface BulkScenarioDraft
    extends BulkScenarioSettings,
        BulkScenarioAssumptions {
    readonly id: string;
    readonly fileName: string;
    readonly title: string;
    readonly uploadNotes: BulkScenarioUploadNotes;
}

/** Fields to merge into a draft, or a function of the draft that returns them. */
export type BulkScenarioDraftPatch =
    | Partial<BulkScenarioDraft>
    | ((draft: BulkScenarioDraft) => Partial<BulkScenarioDraft>);

interface BulkScenarioFile {
    readonly id: string;
    readonly fileName: string;
}

export interface RejectedBulkScenarioFile extends BulkScenarioFile {
    readonly reason: string;
}

export interface ReadBulkScenarioFile extends BulkScenarioFile {
    readonly result: SensitivitiesCsvResult;
}

interface AcceptedBulkScenarioFile extends BulkScenarioFile {
    readonly csv: SensitivitiesCsvAssumptions;
}

export const DUPLICATE_FILE_REASON =
    "A file with this name is already in the table. Remove it first to upload a new version.";

export const scenarioLimitReason = (maxScenarios: number): string =>
    `Bulk upload is limited to ${maxScenarios} scenarios at a time. Create these scenarios, then upload the rest.`;

const fileNameKey = (fileName: string): string => fileName.toLowerCase();

const rejectFile = (
    { id, fileName }: BulkScenarioFile,
    reason: string
): RejectedBulkScenarioFile => ({ id, fileName, reason });

/**
 * Split a drop into the files to read and the files rejected without reading.
 * A name already used by a draft or an earlier file in the drop is a duplicate, compared without case.
 * Once the table holds `maxScenarios` drafts, every other file is rejected for the limit.
 */
export const screenBulkScenarioFiles = <F extends BulkScenarioFile>({
    draftFileNames,
    files,
    maxScenarios
}: {
    readonly draftFileNames: ReadonlyArray<string>;
    readonly files: ReadonlyArray<F>;
    readonly maxScenarios: number;
}): {
    readonly toRead: ReadonlyArray<F>;
    readonly rejectedFiles: ReadonlyArray<RejectedBulkScenarioFile>;
} => {
    const isFull = draftFileNames.length >= maxScenarios;
    const takenNames = new Set(draftFileNames.map(fileNameKey));
    const toRead: F[] = [];
    const rejectedFiles: RejectedBulkScenarioFile[] = [];
    files.forEach((file) => {
        const name = fileNameKey(file.fileName);
        if (takenNames.has(name)) {
            rejectedFiles.push(rejectFile(file, DUPLICATE_FILE_REASON));
        } else if (isFull) {
            rejectedFiles.push(
                rejectFile(file, scenarioLimitReason(maxScenarios))
            );
        } else {
            toRead.push(file);
        }
        takenNames.add(name);
    });
    return { toRead, rejectedFiles };
};

/**
 * Give the open draft slots to the earliest readable files in drop order.
 * Unreadable files keep their own reason and take no slot. Readable files past the last slot are rejected for the limit.
 */
export const fillBulkScenarioSlots = ({
    draftCount,
    files,
    maxScenarios
}: {
    readonly draftCount: number;
    readonly files: ReadonlyArray<ReadBulkScenarioFile>;
    readonly maxScenarios: number;
}): {
    readonly accepted: ReadonlyArray<AcceptedBulkScenarioFile>;
    readonly rejectedFiles: ReadonlyArray<RejectedBulkScenarioFile>;
} => {
    const openSlots = maxScenarios - draftCount;
    const accepted: AcceptedBulkScenarioFile[] = [];
    const rejectedFiles: RejectedBulkScenarioFile[] = [];
    files.forEach((file) => {
        if (!file.result.ok) {
            rejectedFiles.push(rejectFile(file, file.result.headerError));
        } else if (accepted.length >= openSlots) {
            rejectedFiles.push(
                rejectFile(file, scenarioLimitReason(maxScenarios))
            );
        } else {
            accepted.push({
                id: file.id,
                fileName: file.fileName,
                csv: file.result
            });
        }
    });
    return { accepted, rejectedFiles };
};

const CSV_EXTENSION = /\.csv$/i;

const scenarioTitleFromFileName = (fileName: string): string =>
    fileName.replace(CSV_EXTENSION, "").trim();

/** Build a draft named after its file, set to run on submit with no custom upgrade. */
export const bulkScenarioDraftFromCsv = ({
    id,
    fileName,
    csv,
    costAssumptions
}: {
    readonly id: string;
    readonly fileName: string;
    readonly csv: SensitivitiesCsvAssumptions;
    readonly costAssumptions: CostAssumption;
}): BulkScenarioDraft => ({
    id,
    fileName,
    title: scenarioTitleFromFileName(fileName),
    projectSizeAssumptions: csv.assumptions,
    includedSpeculativeProjectIds: csv.includedSpeculativeProjectIds,
    uploadNotes: {
        invalidProjectIds: csv.invalidProjectIds,
        nonEditableProjectIds: csv.nonEditableProjectIds,
        priorQueueErisNormalizedIds: csv.priorQueueErisNormalizedIds,
        wholeProjectOnlyDroppedIds: csv.wholeProjectOnlyDroppedIds,
        withdrawnKeptExcludedIds: csv.withdrawnKeptExcludedIds,
        withdrawnReinstatedIds: csv.withdrawnReinstatedIds,
        receivedReductions: csv.receivedReductions
    },
    costAssumptions,
    customUpgradeId: null,
    runOnSubmit: true
});

/** The setting every draft shares, or undefined when they differ or there are none. */
export const sharedBulkScenarioSetting = <K extends keyof BulkScenarioSettings>(
    drafts: ReadonlyArray<BulkScenarioDraft>,
    key: K
): BulkScenarioSettings[K] | undefined => {
    const [first, ...rest] = drafts;
    if (first === undefined) {
        return undefined;
    }
    return rest.every((draft) => isEqual(draft[key], first[key]))
        ? first[key]
        : undefined;
};

export enum BulkScenarioNoticeKind {
    InvalidProjects = "invalid_projects",
    NonEditableProjects = "non_editable_projects",
    PriorQueueErisNormalized = "prior_queue_eris_normalized",
    WholeProjectOnlyDropped = "whole_project_only_dropped",
    WithdrawnKeptExcluded = "withdrawn_kept_excluded",
    WithdrawnReinstated = "withdrawn_reinstated"
}

export interface BulkScenarioNotice {
    readonly kind: BulkScenarioNoticeKind;
    readonly intent: Intent;
    readonly title: string;
    readonly description: string;
    readonly projectIds: ReadonlyArray<string>;
}

const projectCount = (count: number): string =>
    `${count} ${count === 1 ? "project" : "projects"}`;

const LISTED_PROJECT_LIMIT = 10;

export const listProjectIds = (projectIds: ReadonlyArray<string>): string =>
    projectIds.length > LISTED_PROJECT_LIMIT
        ? `${projectIds.slice(0, LISTED_PROJECT_LIMIT).join(", ")} and ${
              projectIds.length - LISTED_PROJECT_LIMIT
          } more`
        : projectIds.join(", ");

/** What the upload rules changed in one file, warnings first. */
export const bulkScenarioNotices = (
    notes: BulkScenarioUploadNotes,
    onlyExclusionSupported: boolean
): ReadonlyArray<BulkScenarioNotice> => {
    const notices: ReadonlyArray<BulkScenarioNotice> = [
        {
            kind: BulkScenarioNoticeKind.InvalidProjects,
            intent: Intent.WARNING,
            title: `${projectCount(notes.invalidProjectIds.length)} skipped`,
            description: onlyExclusionSupported
                ? "Check the project ID is valid."
                : "Check the project ID is valid and the reductions are numbers between 0 and 100.",
            projectIds: notes.invalidProjectIds
        },
        {
            kind: BulkScenarioNoticeKind.PriorQueueErisNormalized,
            intent: Intent.WARNING,
            title: `${projectCount(
                notes.priorQueueErisNormalizedIds.length
            )} set to 100% ERIS withdrawal`,
            description:
                "Prior-queue projects support full ERIS withdrawal only.",
            projectIds: notes.priorQueueErisNormalizedIds
        },
        {
            kind: BulkScenarioNoticeKind.WholeProjectOnlyDropped,
            intent: Intent.WARNING,
            title: `${projectCount(
                notes.wholeProjectOnlyDroppedIds.length
            )} left in service`,
            description:
                "These projects can only be modelled in service (0%) or dropped (100%), so their reduction was ignored.",
            projectIds: notes.wholeProjectOnlyDroppedIds
        },
        {
            kind: BulkScenarioNoticeKind.NonEditableProjects,
            intent: Intent.PRIMARY,
            title: `${projectCount(
                notes.nonEditableProjectIds.length
            )} can't be adjusted in this study`,
            description:
                "The study fixes these projects, so their rows were skipped.",
            projectIds: notes.nonEditableProjectIds
        },
        {
            kind: BulkScenarioNoticeKind.WithdrawnKeptExcluded,
            intent: Intent.PRIMARY,
            title: `${projectCount(
                notes.withdrawnKeptExcludedIds.length
            )} withdrawn and excluded by default`,
            description: onlyExclusionSupported
                ? "The CSV does not list them. In this stage a CSV cannot re-add them."
                : "The CSV does not list them. To include one, give it a reduction other than 100%.",
            projectIds: notes.withdrawnKeptExcludedIds
        },
        {
            kind: BulkScenarioNoticeKind.WithdrawnReinstated,
            intent: Intent.PRIMARY,
            title: `${projectCount(
                notes.withdrawnReinstatedIds.length
            )} withdrawn but included`,
            description:
                "The CSV gives them a reduction other than 100%, so they stay in the scenario.",
            projectIds: notes.withdrawnReinstatedIds
        }
    ];
    return notices.filter((notice) => notice.projectIds.length > 0);
};

export const bulkScenarioWarningCount = (
    notes: BulkScenarioUploadNotes
): number =>
    notes.invalidProjectIds.length +
    notes.priorQueueErisNormalizedIds.length +
    notes.wholeProjectOnlyDroppedIds.length;
